import React from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Check, ShieldCheck } from "lucide-react";
import { services, safeRide } from "../mock";

const PageHeader = ({ eyebrow, title, subtitle }) => (
  <section className="relative pt-36 pb-16 bg-[#0a121a] border-b border-white/5 overflow-hidden">
    <div className="absolute -top-24 left-1/2 -translate-x-1/2 h-64 w-[40rem] rounded-full bg-[var(--gold)]/8 blur-3xl" />
    <div className="relative max-w-4xl mx-auto px-5 text-center">
      <p className="reveal font-display text-xs uppercase tracking-[0.4em] text-[var(--gold)] mb-4">{eyebrow}</p>
      <h1 className="reveal font-display text-4xl sm:text-6xl font-extrabold text-white">{title}</h1>
      {subtitle && <p className="reveal mt-5 text-lg text-muted-foreground max-w-2xl mx-auto">{subtitle}</p>}
    </div>
  </section>
);

const Services = () => {
  const all = [
    ...services,
    {
      id: "saferide",
      title: "Shadow Path SafeRide",
      tagline: "Luxury pickup & car return service",
      long:
        "Enjoy your night worry-free. Our chauffeurs pick you up in a luxury Escalade while a second driver returns your own car safely to your location — discreet, professional, and reliable.",
      image: safeRide.heroImage,
      price: "",
      to: "/saferide",
    },
  ];

  return (
    <div>
      <PageHeader
        eyebrow="Curated for Comfort"
        title="Our Premium Services"
        subtitle="Whatever the occasion, arrive in effortless luxury with a professional chauffeur and our signature blacked-out Escalade fleet."
      />

      <section className="py-20">
        <div className="max-w-6xl mx-auto px-5 lg:px-8 space-y-16">
          {all.map((s, idx) => (
            <div
              key={s.id}
              className={`reveal grid lg:grid-cols-2 gap-10 items-center ${idx % 2 === 1 ? "lg:[direction:rtl]" : ""}`}
            >
              <div className="img-zoom relative rounded-2xl overflow-hidden border border-white/10 [direction:ltr]">
                <img src={s.image} alt={s.title} className="w-full h-[340px] object-cover" />
                <div className="absolute inset-0 bg-gradient-to-t from-[#0c1620]/60 to-transparent" />
                {s.price && (
                  <span className="absolute top-4 left-4 rounded-full bg-[var(--gold)] px-4 py-1.5 text-xs font-bold uppercase tracking-widest text-[#0c1620]">
                    {s.price}{s.note ? ` · ${s.note}` : ""}
                  </span>
                )}
              </div>
              <div className="[direction:ltr]">
                <p className="font-display text-xs uppercase tracking-[0.35em] text-[var(--gold)] mb-3">{s.tagline}</p>
                <h2 className="font-display text-3xl sm:text-4xl font-extrabold text-white">{s.title}</h2>
                <p className="mt-5 text-muted-foreground leading-relaxed">{s.long}</p>
                <ul className="mt-6 space-y-2">
                  {["Professional licensed chauffeur", "Immaculate luxury Escalade", "Complimentary bottled water & Wi-Fi"].map((f) => (
                    <li key={f} className="flex items-center gap-3 text-sm text-gray-200">
                      <Check size={16} className="text-[var(--gold)]" /> {f}
                    </li>
                  ))}
                </ul>
                <div className="mt-8 flex flex-wrap gap-3">
                  <Link to="/book" className="btn-gold inline-flex items-center gap-2 rounded-full px-6 py-3 text-sm font-semibold uppercase tracking-widest">
                    Book This Service <ArrowRight size={15} />
                  </Link>
                  {s.to && (
                    <Link to={s.to} className="btn-ghost-gold inline-flex items-center gap-2 rounded-full px-6 py-3 text-sm font-semibold uppercase tracking-widest">
                      Learn More
                    </Link>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* Add-ons teaser */}
      <section className="py-16 bg-[#0a121a] border-y border-white/5">
        <div className="max-w-4xl mx-auto px-5 text-center">
          <ShieldCheck className="mx-auto text-[var(--gold)]" size={34} />
          <h2 className="mt-4 font-display text-3xl font-extrabold text-white">Luxury Add-Ons</h2>
          <p className="mt-4 text-muted-foreground">Enhance your ride with champagne &amp; coolers, fresh roses, red-carpet service, and more premium touches.</p>
          <Link to="/luxury-add-ons" className="btn-gold mt-7 inline-flex items-center gap-2 rounded-full px-7 py-3.5 text-sm font-semibold uppercase tracking-widest">
            Explore Add-Ons <ArrowRight size={15} />
          </Link>
        </div>
      </section>
    </div>
  );
};

export default Services;
